import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanLoad,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree,
} from '@angular/router';
import { AuthService } from '@src/app/core/services/auth.service';
import { FeatureFlagService } from '@src/app/core/services/feature-flag.service';
import { UsersService } from '@src/app/admin/users/services/users.service';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagGuard implements CanActivate, CanLoad {
    constructor(
        private readonly featureFlagService: FeatureFlagService,
        private readonly usersService: UsersService,
        private readonly authService: AuthService,
        private readonly router: Router,
    ) {}

    public canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        return this.check(route.data?.featureFlag, state.url);
    }

    public canLoad(
        route: Route,
        segments: UrlSegment[],
    ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        return this.check(route.data?.featureFlag, '/');
    }

    private check(flagKey: string | undefined, redirectUrl: string): Observable<boolean> {
        if (!flagKey) {
            return of(true);
        }

        const user = this.usersService.user;
        const tenant = this.authService.tenant;

        const ensureClient$ = this.featureFlagService.client
            ? of(void 0)
            : from(this.featureFlagService.initialize(user, tenant)).pipe(
                catchError(() => of(void 0)),
            );

        return ensureClient$.pipe(
            switchMap(() => this.featureFlagService.getFlag(flagKey, false)),
            take(1),
            map((enabled) => {
                if (!Boolean(enabled)) {
                    this.router.navigate(['/app'], { queryParams: { redirectUrl } }).then(r => r);
                    return false;
                }

                return true;
            }),
        );
    }
}
